import { SwipeableDrawer } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { AgreementCheckboxes } from "../../components/reservation-page/agreement-checkboxes";
import { ReservationBottomBar } from "../reservationPage/ReservationBottomBar";
import { ContactInformation } from "../../components/reservation-page/contact-information";
import { OrderInfo } from "../../components/reservation-page/order-info";
import { Divider } from "../../common";
import { PageLayout, LeftColumn, RightColumn } from "../reservationPage";
import { PaymentButtons } from "../reservationPage/PaymentButtons";
import { useCreatePayHandler } from "../reservationPage/hooks/useCreatePayHandler";
import { useOrderStatusRedirect } from "../reservationPage/hooks/useOrderStatusRedirect";
import { usePaymentMethods } from "../reservationPage/hooks/usePaymentMethods";
import { PaymentOverlay } from "../reservationPage/PaymentOverlay";
import {
  ReservationPageScopedGlobalStyle,
  OlympiacosReservationScopedGlobalStyle,
  PageContainer,
  TopContainer,
  BottomContainer,
  Actions,
  SummaryDrawerContent,
  SummaryDrawerPuller,
  SummaryDrawerScrollable,
  BREAKPOINT_RESERVATION_PAGE,
} from "../reservation-shared-styles";
import { ReservationModals } from "../reservationPage/ReservationModals";
import { useReservationModals } from "../reservationPage/hooks/useReservationModals";
import { useReservationForm } from "../reservationPage/hooks/useReservationForm";
import { useReservationData } from "../reservationPage/hooks/useReservationData";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { useOrderTotal } from "../reservationPage/hooks/useOrderTotal";
import {
  getAttachmentUrl,
  getCulture,
  getLocaleSegment,
  getPlayUrl,
  mainUrl,
} from "../../utils/Utils";
import { getReservationDisplayDate } from "../reservationPage/utils/getReservationDisplayDate";
import { useFormSessionRestore } from "../reservationPage/hooks/useFormSessionRestore";
import { useReservationPayload } from "../reservationPage/hooks/useReservationPayload";
import {
  FinalizeErrorCode,
  isOlympiacosAccessError,
  useSubmitReservation,
} from "../reservationPage/hooks/useSubmitReservation";
import { ReservationIntent } from "../../common/enums/ReservationIntent";
import { OrderStatus } from "../../common/enums/OrderStatus";
import { useFieldErrorMapper } from "../reservationPage/hooks/useFieldErrorMapper";
import { TicketInfo } from "../../components/reservation-page/ticket-holders/ticket-info";
import { PRODUCER_ID_OLYMPIACOS } from "../../common/producerIds";
import { Billing } from "../../components/reservation-page/billing";
import { ErrorPage } from "../../components/common/ErrorPage";
import { useUserData } from "../../hooks/useUserData";
import { useMaybeUpdateUser } from "../../hooks/useMaybeUpdateUser";
import { TicketSelection } from "./ticket-selection";
import { CenteredOverlay } from "../../components/common/CenteredOverlay";
import { InfoModal } from "../reservationPage/InfoModal";
import { Warning } from "../../components/icons";
import { useSplitOrderResources } from "./hooks/useSplitOrderResources";
import useModal from "../../hooks/useModal";
import SplitOrderSkeleton from "../../components/Skeletons/SplitOrderSkeleton";
import { MoreTheme, OlympiacosTheme, PaobcTheme, colors } from "../../components/common/mixins";
import styled from "styled-components";
import useMiniSite from "../../hooks/useMiniSite";
import SitePolicyPrompt from "../../components/SitePolicyPrompt.jsx";
import { useScrollToFirstError } from "../reservationPage/hooks/useScrollToFirstError";


function SplitOrderModal({
  open,
  id,
  title,
  description,
  buttonText,
  onClose,
}: {
  open: boolean;
  id: string;
  title: string;
  description: string;
  buttonText: string;
  onClose: () => void;
}) {
  return (
    <CenteredOverlay
      handleClose={onClose}
      open={open}
      size="sm"
      id={id}
      titleText={title}
      icon={<Warning />}
    >
      <InfoModal
        description={description}
        buttonText={buttonText}
        onClick={onClose}
      />
    </CenteredOverlay>
  );
}

// ErrorPage retry remounts the page via `key` so form, modal and selection state
// start fresh and data is refetched. Unlike a full reload, the global reservation
// store survives until the new fetch resets it.
export function SplitOrderApp() {
  const [attempt, setAttempt] = useState(0);
  return (
    <SplitOrderPageContent
      key={attempt}
      onRetry={() => setAttempt((n) => n + 1)}
    />
  );
}

function SplitOrderPageContent({ onRetry }: { onRetry: () => void }) {
  const r = useSplitOrderResources();

  const {
    data,
    loading: reservationLoading,
    error,
    refetch,
  } = useReservationData();

  const { userData, isLoggedIn, loading: userLoading } = useUserData();
  const [isTicketWarningOpen, setIsTicketWarningOpen] =
    useState(false);
  const [ticketSelectionError, setTicketSelectionError] = useState(false);

  const { isOlympiacos, isPaoBC } = useMiniSite();
  const isUnder900 = useMediaQuery(
    `(max-width: ${BREAKPOINT_RESERVATION_PAGE})`,
  );

  const sitePolicyPromptRef = useRef<{ open: (reason?: string) => void }>(null);
  const ticketSelectionRef = useRef<HTMLDivElement>(null);

  const { maybeUpdateUser } = useMaybeUpdateUser(
    userData,
    isLoggedIn,
  );

  const tickets = data?.tickets ?? [];
  const ticketPersonFormFields = data?.ticketPersonFormFields ?? [];

  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<number>>(
    new Set(),
  );
  const selectedTickets = useMemo(
    () => tickets.filter((t) => selectedTicketIds.has(t.id)),
    [tickets, selectedTicketIds],
  );
  const merchantTerms = data?.merchantTerms ?? [];
  const status = data?.status;

  const playUrl = data?.playUrl
    ? getPlayUrl(data.playUrl)
    : `${mainUrl()}/${getLocaleSegment()}/`;

  const imageUrl = data?.eventImage?.imageUrl
    ? getAttachmentUrl(data.eventImage.imageUrl)
    : null;

  const producerName = data?.producer?.name;
  const isOlympiacosEvent = data?.producer?.id === PRODUCER_ID_OLYMPIACOS;
  const currency = data?.currency;

  const venue = data?.venue;
  const playTitle = data?.playTitle;
  const showBilling = data?.allowInvoicing && data?.totalPrice !== 0;
  const date = getReservationDisplayDate(data, getCulture());

  const paymentMethods = data?.paymentMethods ?? [];
  const returnUrl = playUrl ? playUrl : "/";
  const { isRedirecting } = useOrderStatusRedirect(status, returnUrl, data?.amountDue);

  const methods = useReservationForm({
    userData,
    ticketPersonFormFields,
    tickets: selectedTickets,
    merchantTerms,
    producerId: data?.producer?.id ?? null,
  });

  const selectedTicketsTotal = selectedTickets.reduce(
    (sum, t) => sum + t.price,
    0,
  );
  const donationAmount = methods.watch("donationAmount") ?? 0;
  const total = useOrderTotal(selectedTicketsTotal, donationAmount);

  const intent =
    selectedTickets.length === tickets.length
      ? ReservationIntent.Finalize
      : ReservationIntent.Split;
  const { buildPayload } = useReservationPayload(selectedTickets, ticketPersonFormFields, intent);
  const {
    submitReservation,
    isSubmitting: isSubmittingReservation,
    error: submitError,
    clearError: clearSubmitError,
  } = useSubmitReservation();

  const {
    isOpen: isEmailLimitModalOpen,
    openModal: openEmailLimitModal,
    closeModal: closeEmailLimitModal,
  } = useModal(false, { disableScrollLock: true });

  const {
    expirationModalOpen,
    handleCloseExpirationModal,
    isPaymentModalOpen,
    openPaymentModal,
    closePaymentModal,
    paymentModalKey,
    setPaymentModalKey,
    isOrderSummaryOpen,
    openOrderSummary,
    closeOrderSummary,
    isGenericErrorModalOpen,
    openGenericErrorModal,
    handleCloseGenericErrorModal,
  } = useReservationModals({
    playUrl,
    onCloseGenericError: clearSubmitError,
  });

  useEffect(() => {
    document.body.style.cursor = isSubmittingReservation ? "wait" : "";
    return () => {
      document.body.style.cursor = "";
    };
  }, [isSubmittingReservation]);

  const { submitCount } = methods.formState;
  useScrollToFirstError(submitCount);

  useFormSessionRestore({
    reset: methods.reset,
    onRestoreSelectedTickets: (ids) => setSelectedTicketIds(new Set(ids)),
  });


  const isFieldMappedError = (err: typeof submitError) =>
    !!(err?.perFieldErrors && Object.keys(err.perFieldErrors).length > 0);

  const isTicketWarningError = (err: typeof submitError) =>
    err?.errorCode === FinalizeErrorCode.IncludesRenewedTickets ||
    err?.errorCode === FinalizeErrorCode.IncludesCancelledTickets;

  const shouldShowGenericError = (err: typeof submitError) =>
    !!err &&
    !isFieldMappedError(err) &&
    !isTicketWarningError(err) &&
    !isOlympiacosAccessError(err?.errorCode) &&
    err?.errorCode !== FinalizeErrorCode.EmailTicketLimitReached;

  const handleSelectionChange = (newSelection: Set<number>) => {
    [...selectedTicketIds]
      .filter((id) => !newSelection.has(id))
      .forEach((id) => methods.clearErrors(`ticketPersonData.${id}` as any));
    if (newSelection.size > 0) setTicketSelectionError(false);
    setSelectedTicketIds(newSelection);
  };

  const handleSubmitError = async (err: typeof submitError) => {
    if (isTicketWarningError(err)) {
      setIsTicketWarningOpen(true);
    } else if (err?.errorCode === FinalizeErrorCode.EmailTicketLimitReached) {
      openEmailLimitModal();
    } else if (isOlympiacosAccessError(err?.errorCode)) {
      sitePolicyPromptRef.current?.open();
    } else if (shouldShowGenericError(err)) {
      openGenericErrorModal();
    }
  };

  useFieldErrorMapper(submitError, methods);

  useEffect(() => {
    if (!isUnder900) {
      closeOrderSummary();
    }
  }, [isUnder900]);

  const billing = useWatch({
    control: methods.control,
    name: "billing",
  });

  const preValidate = (): boolean => {
    if (selectedTickets.length === 0) {
      setTicketSelectionError(true);
      ticketSelectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  };

  const { createPayHandler, submittingButton } = useCreatePayHandler({
    handleSubmit: methods.handleSubmit,
    closePaymentModal,
    submitReservation,
    buildPayload,
    selectedTicketIds: [...selectedTicketIds],
    onError: handleSubmitError,
    preValidate,
    beforeSubmit: async (values) => {
      void maybeUpdateUser(
        values.contact.mobile,
        values.contact.mobileCountryCode ?? "",
        values.saveInfoToAccount,
        values.contact.firstName,
        values.contact.lastName,
        values.contact.email,
      ).catch(() => {});
    },
  });

  const {
    payNowMethod,
    apiMethod,
    payLaterMethod,
    irisMethod,
    isPayNowActive,
    hasSecondaryPaymentOptions,
  } = usePaymentMethods(paymentMethods, total, billing?.type === "invoice");

  const handlePayNow = createPayHandler(
    "payNow",
    payNowMethod?.active ? (payNowMethod?.name ?? "") : (apiMethod?.name ?? ""),
  );
  const handlePayLater = createPayHandler(
    "payLater",
    payLaterMethod?.name ?? "",
  );
  const handlePayWithIris = createPayHandler("iris", irisMethod?.name ?? "");

  const loading = reservationLoading || userLoading;

  // Loading state
  if (loading) {
    return (
      <PageContainer>
        <SplitOrderSkeleton />
      </PageContainer>
    );
  }

  if (error || !data) {
    return <ErrorPage onRetry={onRetry} />;
  }

  const isDisabled = isSubmittingReservation || isRedirecting;

  return (
    <>
      <MoreTheme />
      {isOlympiacos && <OlympiacosTheme />}
      {isPaoBC && <PaobcTheme />}
      <SitePolicyPrompt
        intent="buy"
        mandatory={false}
        onClose={() => {
          window.location.href = `${mainUrl()}/${getLocaleSegment()}/tickets/`;
        }}
        ref={sitePolicyPromptRef}
      />
      <FormProvider {...methods}>
        <PageContainer>
          <ReservationPageScopedGlobalStyle />
          {isOlympiacos && <OlympiacosReservationScopedGlobalStyle />}
          <PageLayout>
            <LeftColumn>
              <TopContainer>
                <ContactInformation
                  userData={userData}
                  isLoggedIn={isLoggedIn}
                />
                <div ref={ticketSelectionRef}>
                  <TicketSelection
                    tickets={tickets}
                    selected={selectedTicketIds}
                    onSelectionChange={handleSelectionChange}
                    isSeasonTicket={data?.isSeasonTicket}
                    error={ticketSelectionError}
                  />
                </div>
                {ticketPersonFormFields.length > 0 &&
                  selectedTickets.length > 0 && (
                    <TicketInfo
                      ticketPersonFormFields={ticketPersonFormFields}
                      tickets={selectedTickets}
                      isOlympiacosEvent={isOlympiacosEvent}
                      // Split order is deliberately products-free.
                      hasPerTicketProducts={false}
                      currency={currency}
                    />
                  )}
                {showBilling && <Billing />}
              </TopContainer>
              <BottomContainer>
                <Divider />

                <AgreementCheckboxes
                  merchantTerms={merchantTerms}
                  producerName={producerName ?? ""}
                />

                <Actions>
                  <PaymentButtons
                    onPayNow={handlePayNow}
                    onPayLater={handlePayLater}
                    onIris={handlePayWithIris}
                    isSubmitting={isSubmittingReservation}
                    submittingButton={submittingButton}
                    isFormDisabled={isDisabled}
                    isPayNowActive={isPayNowActive}
                    isPayLaterActive={payLaterMethod?.active}
                    isIrisActive={irisMethod?.active}
                    isFreeOrder={total === 0}
                    hasSecondaryOptions={hasSecondaryPaymentOptions}
                  />
                </Actions>
              </BottomContainer>
            </LeftColumn>

            <RightColumn>
              <OrderInfo
                tickets={selectedTickets}
                ticketsTotal={total}
                venue={venue}
                date={date}
                playTitle={playTitle}
                playUrl={playUrl}
                imageUrl={imageUrl}
                isUnder900={isUnder900}
                paidAmount={data?.paidAmount ?? 0}
              />
            </RightColumn>
          </PageLayout>

          <ReservationBottomBar
            isOrderSummaryOpen={isOrderSummaryOpen}
            onToggleOrderSummary={
              isOrderSummaryOpen ? closeOrderSummary : openOrderSummary
            }
            total={total}
            currency={currency}
            isDisabled={isDisabled}
            isArrowDisabled={selectedTickets.length === 0}
            hasSecondaryPaymentOptions={hasSecondaryPaymentOptions}
            isSubmittingReservation={isSubmittingReservation}
            onPayNow={() => {
              closeOrderSummary();
              handlePayNow();
            }}
            onOpenPaymentModal={() => {
              closeOrderSummary();
              setPaymentModalKey((k) => k + 1);
              openPaymentModal();
            }}
          />

          {/* Drawer Start */}
          {isUnder900 && (
            <SwipeableDrawer
              anchor="bottom"
              open={isOrderSummaryOpen}
              onOpen={openOrderSummary}
              onClose={closeOrderSummary}
              disableSwipeToOpen
              disableScrollLock // page scroll is locked by useModal
              PaperProps={{ sx: { bottom: "var(--bottom-bar-height, 73px)" } }} // position drawer, so it doesnt overlap with BottomBar
            >
              <SummaryDrawerContent>
                <SummaryDrawerPuller />
                <SummaryDrawerScrollable>
                  <OrderInfo
                    tickets={selectedTickets}
                    ticketsTotal={total}
                    venue={venue}
                    date={date}
                    playTitle={playTitle}
                    playUrl={playUrl}
                    imageUrl={imageUrl}
                    isSummaryDrawer
                    paidAmount={data?.paidAmount ?? 0}
                  />
                </SummaryDrawerScrollable>
              </SummaryDrawerContent>
            </SwipeableDrawer>
          )}

          <PaymentOverlay
            isOpen={isPaymentModalOpen}
            onClose={closePaymentModal}
            paymentModalKey={paymentModalKey}
            isUnder900={isUnder900}
            onPayNow={handlePayNow}
            onPayLater={handlePayLater}
            onIris={handlePayWithIris}
            isSubmitting={isSubmittingReservation}
            submittingButton={submittingButton}
            isFormDisabled={isDisabled}
            isPayNowActive={isPayNowActive}
            isPayLaterActive={payLaterMethod?.active}
            isIrisActive={irisMethod?.active}
            isFreeOrder={total === 0}
            hasSecondaryOptions={hasSecondaryPaymentOptions}
          />
          {/* Drawer End */}

          <ReservationModals
            expirationModalOpen={expirationModalOpen}
            onCloseExpiration={handleCloseExpirationModal}
            isEmailLimitModalOpen={isEmailLimitModalOpen}
            onCloseEmailLimit={closeEmailLimitModal}
            emailLimitDescription={submitError?.message ?? ""}
            emailLimitExceeded={(submitError?.emailLimitRemaining ?? 0) > 0}
            isGenericErrorModalOpen={isGenericErrorModalOpen}
            onCloseGenericError={handleCloseGenericErrorModal}
          />

          <SplitOrderModal
            open={isTicketWarningOpen}
            id="ticket-warning"
            title={data?.isSeasonTicket ? r.RenewalWarningTitle : r.IssueWarningTitle}
            description={data?.isSeasonTicket ? r.RenewalWarningText : r.IssueWarningText}
            buttonText={r.Continue}
            onClose={() => {
              setIsTicketWarningOpen(false);
              void refetch();
            }}
          />

          <SplitOrderModal
            open={tickets.length === 0 || status === OrderStatus.Canceled}
            id="completed"
            title={data?.isSeasonTicket ? r.AlreadyRenewedTitle : r.AlreadyIssuedTitle}
            description={data?.isSeasonTicket ? r.AlreadyRenewedText : r.AlreadyIssuedText}
            buttonText={r.BackToEvent}
            onClose={() => {
              window.location.href = playUrl;
            }}
          />
        </PageContainer>
      </FormProvider>
    </>
  );
}
